import './index.less';

import { AI_CREDITS_OPTIONS, CUSTOM_BUNDLE_INFO, PLAN_MODULE_CARDS } from '@/shared/constants/subscription';
import { BillingOption, PietraPlanModuleCard, PietraPlanModuleType } from '@/shared/types/subscription';
import { find, kebabCase } from 'lodash-es';
import { formatCurrency, goToContactSales } from '@/shared/utils/utils';
import { useCallback, useMemo, useState } from 'react';
import { useConfigProvider, useCreateAccountProvider } from '@/lib/context';

import { CheckOutlined } from '@pietra/icons';
import { EventSource } from '@/shared/constants/event';
import { StoreModuleItem } from '@/lib/service/pietra-v2';
import cN from 'classnames';
import { getMonthlyPriceForModule } from '@/shared/utils/subscription-pricing';
import { logCommonView } from '@/lib/service';

interface IProps {
  moduleType: PietraPlanModuleType;
  storeModuleItems: StoreModuleItem[];
  billingOption?: BillingOption;
}

const BundleCard = ({ moduleType, storeModuleItems, billingOption = BillingOption.YEAR }: IProps) => {
  const { showSignupDialog } = useCreateAccountProvider();
  const { isMobile } = useConfigProvider();
  const isCustomBundle = moduleType === PietraPlanModuleType.CUSTOM;
  const isBeginnerBundle = moduleType === PietraPlanModuleType.BEGINNER_BUNDLE;
  const credits = useMemo(() => AI_CREDITS_OPTIONS[moduleType], [moduleType]);
  const [selectedCredits] = useState(credits[0]);

  const storeModuleItem = isCustomBundle
    ? CUSTOM_BUNDLE_INFO
    : storeModuleItems.find((moduleItem) => moduleItem.name === moduleType);
  const moduleCard = find(PLAN_MODULE_CARDS, (card: PietraPlanModuleCard) => card.moduleType === moduleType);
  const monthlyPrice = isCustomBundle
    ? 'Custom'
    : getMonthlyPriceForModule(storeModuleItem as StoreModuleItem, billingOption, selectedCredits);
  const billingOptionText = billingOption === BillingOption.YEAR ? 'yearly' : 'monthly';

  const headerInfo: { text: string; background: string } = useMemo(() => {
    switch (moduleType) {
      case PietraPlanModuleType.PROFESSIONAL_BUNDLE:
        return {
          text: 'Most popular',
          background: '#000',
        };
      default:
        return null;
    }
  }, [moduleType]);

  const goToPreviewFlow = useCallback(() => {
    logCommonView(EventSource.BOOK_A_DEMO, EventSource.BOOK_A_DEMO, isMobile);
    goToContactSales();
  }, [isMobile]);

  const handleClickCta = useCallback(() => {
    if (isCustomBundle) {
      goToPreviewFlow();
    } else {
      showSignupDialog?.();
    }
  }, [goToPreviewFlow, isCustomBundle, showSignupDialog]);

  return (
    <div
      className={cN(
        'bundle-card',
        {
          'has-header': !!headerInfo,
        },
        kebabCase(moduleType),
      )}
    >
      {headerInfo && (
        <div className="header">
          <span>{headerInfo.text}</span>
        </div>
      )}
      <div className="inner">
        <div className="text">
          <h3 className="title">{storeModuleItem.title}</h3>
        </div>
        <div className="price">
          <span>
            {isCustomBundle
              ? monthlyPrice
              : formatCurrency(monthlyPrice as number, /* containsFractionalDigits */ false)}
            <span className="postfix">/mo</span>
          </span>
          <span>
            {isCustomBundle
              ? `With a yearly commitment`
              : `Billed ${billingOptionText}${isBeginnerBundle ? '. Limited-time offer.' : ''}`}
          </span>
        </div>
        <div className="attributes">
          {moduleCard.attributeHeader && (
            <div className="attribute title">
              <span>{moduleCard.attributeHeader}</span>
            </div>
          )}
          {moduleCard.moduleAttributes.map((attribute: string, i: number) => (
            <div className="attribute" key={i}>
              <CheckOutlined className="icon" />
              <span>{attribute}</span>
            </div>
          ))}
        </div>
        <div className="create-account" onClick={handleClickCta}>
          <span>{isCustomBundle ? 'Request a demo' : 'Start free trial'}</span>
        </div>
      </div>
    </div>
  );
};

export default BundleCard;
